// client/src/components/layout/sidebar.tsx
import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, LogOut, Settings, ShieldCheck, Sparkles } from "lucide-react";
import { OFFICIAL_CHANNELS_PATH } from "@shared/lib/communication-reference";
import { cn } from "@/lib/cn";
import { handleLogout } from "@/lib/logout";
import { OrgSwitcher } from "@/components/org-switcher";
import { useOrgContext } from "@/lib/use-org-context";
import { useCurrentUser } from "@/lib/use-current-user";
import { protectionLevel } from "@/lib/terminology";
import { CHANGELOG_PATH, CHANGELOG_TITLE } from "@/lib/changelog-view";
import { isPlatformRoute } from "@/lib/route-context";
import { NavBadge } from "./nav-badge";
import { NavLockGlyph } from "./nav-lock-glyph";
import { buildNavItems, buildPlatformNavItems, isBelowMinAal, isNavItemActive, useVisibleNavItems } from "./nav-items";
import { PRODUCT_NAME } from "@shared/brand";

interface SidebarProps {
  aal?: "AAL1" | "AAL2" | "AAL3";
  displayName?: string;
}

// Shared "emerald chip" shape — used by both the brand shield (expanded
// header) and the collapsed toggle button, which stand in for each other and
// are never rendered at the same time. One definition keeps the two visually
// identical instead of two class strings that can silently drift apart.
const EMERALD_BADGE_CLASSES =
  "flex h-7 w-7 items-center justify-center rounded-lg border border-strenn-emerald/30 bg-strenn-emerald/10";

// Footer items (Settings, What's new, Official channels, Sign out) share one
// shape, named once so the four cannot drift apart.
const FOOTER_ITEM_CLASSES = cn(
  "flex items-center gap-3 px-4 h-10 w-full rounded-[var(--radius-sm)]",
  "text-text-tertiary hover:text-text-secondary hover:bg-bg-overlay",
  "transition-colors duration-[var(--duration-fast)]",
);

export function Sidebar({ aal, displayName }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("sidebar-collapsed") === "true";
  });
  const [location] = useLocation();
  const { currentContext } = useOrgContext();
  const { user } = useCurrentUser();
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", String(collapsed));
  }, [collapsed]);

  const onPlatform = isPlatformRoute(location);
  // `role` is read here — the only place these plain builder functions can
  // reach a hook — and threaded into buildPlatformNavItems so it can tighten
  // its gate to operators only. Which profile is active (Personal/Org vs
  // Platform Console) is decided ENTIRELY by `onPlatform` — a platform-admin
  // role never adds a Platform Console tab to the Personal/Org list, and a
  // non-operator role never sees platform tabs while on /admin/platform*.
  const items = useVisibleNavItems(
    onPlatform ? buildPlatformNavItems(user?.role) : buildNavItems(currentContext),
  );

  return (
    <motion.aside
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{
        duration: prefersReducedMotion ? 0 : 0.22,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={cn(
        "h-dvh flex flex-col border-r border-border-default",
        "shrink-0 overflow-hidden",
      )}
      // Theme tokens, not hex: #1674's fixed navy stayed dark in light mode.
      style={{ backgroundImage: "linear-gradient(var(--color-bg-elevated), var(--color-bg-primary))" }}
    >
      {/* Header — emerald branded shield mark + wordmark. Emerald-forward brand
          direction (owner-approved 2026-07-16) to match the onboarding flow;
          delivered via the strenn-emerald token so it stays within the
          design-token gate. EMERALD_BADGE_CLASSES is shared by the brand
          shield (expanded) and the toggle button (collapsed) — they're never
          both on screen at once, so this is one chip definition per state,
          not duplication; keep it a single constant so a future token/radius
          change only needs editing once. */}
      <div
        className={cn(
          "h-14 flex items-center border-b border-border-subtle",
          collapsed ? "justify-center" : "gap-2 px-4",
        )}
      >
        {!collapsed && (
          <>
            <span className={cn(EMERALD_BADGE_CLASSES, "shrink-0")}>
              <ShieldCheck size={16} className="text-strenn-emerald" aria-hidden="true" />
            </span>
            <span className="text-[length:var(--text-subheading-size)] font-semibold text-text-primary truncate">
              {PRODUCT_NAME}
            </span>
          </>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "rounded-[var(--radius-sm)] hover:bg-bg-overlay",
            collapsed
              ? cn(EMERALD_BADGE_CLASSES, "text-strenn-emerald hover:border-strenn-emerald/50")
              : "ml-auto p-2 text-text-tertiary hover:text-text-primary",
          )}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <div
        className={cn(
          "border-b border-border-subtle",
          collapsed ? "py-2" : "px-3 py-2",
        )}
      >
        <OrgSwitcher compact={collapsed} />
      </div>

      {/* Nav — plain DOM rendering only.
       *
       * Issue #373 history: an earlier implementation wrapped the per-context
       * item list in <AnimatePresence> with motion.div fade+slide enter/exit
       * animations, falling back to plain DOM only under prefers-reduced-motion.
       * Under normal motion that path was broken: exiting items never
       * unmounted (Guardians + Personal Vault stayed in DOM at opacity:1 with
       * 40px height each, producing a visible vertical gap), and entering
       * items got stuck at their initial state (opacity:0). Root cause was
       * AnimatePresence's keyed-children machinery getting confused by the
       * combination of `initial={false}` + per-child `initial` props + child
       * <Link> components from wouter re-rendering on every URL change.
       *
       * The fix: render the nav as plain DOM unconditionally. Context switch
       * becomes a single native React reconciliation — no presence-tracking,
       * no stuck items, no gap. The animation budget the spec called for
       * (≤350ms enter/exit per DESIGN-SYSTEM.md) is acceptable to forgo
       * because (a) reduced-motion users got plain-DOM anyway, (b) it's the
       * only path that actually worked, and (c) a sidebar nav re-render is
       * not a vestibular-trigger event worth complicating.
       */}
      <nav className="flex-1 py-2" role="navigation">
        {items.map((item) => {
          const active = isNavItemActive(item, location);
          // Lock affordance (AC-7, #1791): display-only signal that the
          // destination enforces a higher floor than the user currently
          // holds. Clicking still navigates — AalGatePanel explains the
          // step-up. Never a raw "AAL3" string (plain-language rule).
          const locked = isBelowMinAal(aal, item.minAal);
          const lockedLabel = locked
            ? `${item.label} — needs ${protectionLevel(item.minAal!).label}`
            : undefined;
          // Accessible name for the interactive element itself (adversarial-
          // review MINOR): unlocked items need SOME name too, not just locked
          // ones — a collapsed sidebar's icon-only item otherwise has none.
          // The <Link>'s aria-label REPLACES its subtree for assistive tech, so
          // the badge's own aria-label would never be announced — the count has
          // to be folded in here or a screen-reader user learns nothing is
          // waiting (#2171).
          const badgeCount = item.badgeCount ?? 0;
          const countSuffix = badgeCount > 0 ? ` — ${badgeCount} pending` : "";
          const accessibleLabel = `${lockedLabel ?? item.label}${countSuffix}`;
          // MAJOR 3 (adversarial review): the lock glyph alone tells a sighted
          // mouse user "locked" but never "needs Level 3, fresh" — the
          // requirement text lived only in aria-label, screen-reader-only.
          // Reuse the canonical §3.10 tooltip primitive (Reuse-First) so the
          // requirement is visible on hover before the click, same as every
          // other metric/badge in the product. Lock glyph itself stays
          // aria-hidden; the tooltip carries the accessible description.
          // NavLockGlyph (shared with bottom-tabs.tsx) so the two navs cannot
          // drift on size/offset — collapsed has no room for an inline
          // sibling, so it overlays the icon's own corner exactly like the
          // bottom tab bar does.
          const lockGlyph = locked ? (
            <NavLockGlyph label={lockedLabel} variant={collapsed ? "overlay" : "inline"} />
          ) : null;
          // Attention count (#2171). Collapsed, the label is gone, so the chip
          // rides the icon corner — otherwise it sits at the end of the row.
          // Rendered by the SAME component bottom-tabs.tsx uses, so the two
          // navs cannot show different counts or different treatments.
          const badge = (
            <NavBadge
              count={item.badgeCount ?? 0}
              label={item.label}
              className={collapsed ? "absolute -right-2 -top-1" : "ml-auto"}
            />
          );
          const inner = (
            <>
              <span className={collapsed ? "relative" : undefined}>
                <item.icon size={20} />
                {collapsed && badge}
                {collapsed && lockGlyph}
              </span>
              {!collapsed && (
                <span className="text-[length:var(--text-body-size)] truncate">{item.label}</span>
              )}
              {!collapsed && badge}
              {!collapsed && lockGlyph}
            </>
          );
          if (item.disabled) {
            // Unreachable today — no NavItem ever sets `disabled` (adversarial
            // review MINOR: kept, not deleted, so a future disabled item gets
            // the same accessible-name treatment as the live branch below for
            // free rather than silently regressing).
            return (
              <div
                key={item.label}
                role="link"
                aria-disabled="true"
                tabIndex={-1}
                aria-label={accessibleLabel}
                className={cn(
                  "flex items-center gap-3 px-4 h-10 mx-2 rounded-[var(--radius-sm)]",
                  "opacity-40 cursor-not-allowed text-text-secondary",
                )}
              >
                {inner}
              </div>
            );
          }
          return (
            <Link
              key={item.label}
              href={item.href}
              aria-label={accessibleLabel}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 px-4 h-10 mx-2 rounded-[var(--radius-md)]",
                "transition-colors duration-[var(--duration-fast)]",
                active
                  ? "bg-strenn-emerald/10 text-strenn-emerald font-medium shadow-[inset_2px_0_0_0_#10B981]"
                  : "text-text-secondary hover:text-text-primary hover:bg-bg-overlay",
              )}
            >
              {inner}
            </Link>
          );
        })}
      </nav>

      {/* Footer — displayName + AAL + Sign Out */}
      <div className="border-t border-border-subtle px-2 py-3 space-y-1">
        {!collapsed && displayName && (
          <div className="px-3 py-2 border-b border-border-subtle mb-1">
            <p className="text-text-primary text-xs font-medium truncate">{displayName}</p>
            {aal && (
              <p
                className="text-text-tertiary text-xs"
                aria-label={`Protection level: ${protectionLevel(aal).short}`}
              >
                {protectionLevel(aal).short}
              </p>
            )}
          </div>
        )}
        <Link href="/settings" title="Settings" aria-label="Settings">
          <div className={FOOTER_ITEM_CLASSES}>
            <Settings size={20} />
            {!collapsed && <span className="text-[length:var(--text-body-size)] truncate">Settings</span>}
          </div>
        </Link>
        <Link href={CHANGELOG_PATH} title={CHANGELOG_TITLE} aria-label={CHANGELOG_TITLE}>
          <div className={FOOTER_ITEM_CLASSES}>
            <Sparkles size={20} />
            {!collapsed && <span className="text-[length:var(--text-body-size)] truncate">{CHANGELOG_TITLE}</span>}
          </div>
        </Link>
        {/* Reachability fallback (AC-90): no dedicated Help surface exists
            yet — this shell footer is the fallback link site. */}
        <Link href={OFFICIAL_CHANNELS_PATH} title="Official channels" aria-label="Official channels">
          <div className={FOOTER_ITEM_CLASSES}>
            <ShieldCheck size={20} />
            {!collapsed && <span className="text-[length:var(--text-body-size)] truncate">Official channels</span>}
          </div>
        </Link>
        <button
          onClick={handleLogout}
          className={cn(FOOTER_ITEM_CLASSES, "h-auto py-2 min-h-[44px]")}
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut size={20} />
          {!collapsed && (
            <span className="text-[length:var(--text-body-size)] truncate">Sign out</span>
          )}
        </button>
      </div>
    </motion.aside>
  );
}
